import React from 'react';
import { Briefcase, MapPin, Calendar, ChevronRight } from 'lucide-react';
import { experience } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';

const ExperienceCard = ({ job, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.1);
  const animationClass = index % 2 === 0 ? 'animate-slide-right' : 'animate-slide-left';

  return (
    <div
      ref={cardRef}
      className={`relative flex flex-col md:flex-row gap-8 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''} ${animationClass} ${cardVisible ? 'visible' : ''}`}
    >
      {/* Timeline Dot */}
      <div className="absolute left-0 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-teal-500 border-4 border-slate-900 hidden md:block" />

      {/* Content Card */}
      <div className={`flex-1 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
        <div className="p-6 md:p-8 rounded-2xl bg-slate-950 border border-slate-800 hover:border-teal-500/30 transition-all duration-300 group">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-xl font-bold text-white group-hover:text-teal-400 transition-colors">
                {job.role}
              </h3>
              <p className="text-teal-400 font-medium">{job.company}</p>
            </div>
            {job.reportingTo && (
              <span className="px-3 py-1 rounded-full bg-teal-500/10 text-teal-400 text-xs font-medium">
                Reports to {job.reportingTo}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-4 mb-6 text-sm text-slate-500">
            {job.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{job.location}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{job.period}</span>
            </div>
          </div>

          {/* Render categorized highlights if available */}
          {job.highlightCategories ? (
            <div className="space-y-5">
              {job.highlightCategories.map((category, cIndex) => (
                <div key={cIndex}>
                  <h4 className="text-teal-400 font-semibold text-sm mb-3 uppercase tracking-wide">
                    {category.title}
                  </h4>
                  <ul className="space-y-2">
                    {category.items.map((item, iIndex) => (
                      <li key={iIndex} className="flex items-start gap-3 text-slate-400 text-sm">
                        <ChevronRight className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-3">
              {job.highlights && job.highlights.map((highlight, hIndex) => (
                <li key={hIndex} className="flex items-start gap-3 text-slate-400 text-sm">
                  <ChevronRight className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Empty space for timeline alignment */}
      <div className="hidden md:block flex-1" />
    </div>
  );
};

const Experience = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);

  return (
    <section id="experience" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <div 
          ref={headerRef}
          className={`text-center mb-16 animate-on-scroll ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 mb-6">
            <Briefcase className="w-4 h-4 text-teal-400" />
            <span className="text-teal-400 text-sm font-medium">Career Journey</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Professional Experience
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            A progression of leadership roles bridging technology, operations, and education
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal-500 via-slate-700 to-slate-800 hidden md:block" />

          <div className="space-y-12">
            {experience.map((job, index) => (
              <ExperienceCard key={job.id} job={job} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
